/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable prefer-destructuring */
import { put, apply, select } from 'redux-saga/effects';
import decode from 'jwt-decode';

import { GTM } from '@app/analytics';
import { config } from '@app/config';
import { client } from '@app/data/iron';
import ironLegacyClient from '@app/data/ironLegacyClient';
import { getOrSetStockXSessionId } from '@app/fraud';
import { EUCountries } from '@app/location';
import { history } from '@app/router';
import { DOM } from '@app/utils';
import type { DecodedJWT } from '@app/types';

import {
  FAVORITE_PRODUCT_REDIRECT_KEY,
  IFavoriteRedirectData,
} from '@app/favorite/constants';
import {
  FOLLOW_PRODUCT_REDIRECT_KEY,
  IFollowRedirectData,
} from '@app/follow/constants';

import { getMeFailure, getMeSuccess, logOutTry } from '@app/redux/auth/actions';
import { setCustomer, clearCustomer } from '@app/redux/customer/actions';

import { getGDPR, getCountryCode } from '../clientLocale/selectors';
import { getSelectedCurrency } from '../currency/selectors';
import { getCustomer } from '../customer/selectors';
import { getUrlPrefix, getUrlCode } from '../localization/selectors';

const isTokenExpired = () => {
  const token = DOM.getCookie<string>('token');
  if (!token) {
    return true;
  }

  const decoded = decode<DecodedJWT>(token);
  if (decoded?.exp) {
    return decoded.exp - Date.now() / 1000 < 10;
  }
};

export function* onGetMeRequest() {
  try {
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 2.
    const response = yield apply(ironLegacyClient, ironLegacyClient.getMe);

    const customer = response?.body?.Customer;

    GTM.loginSignup('identify', customer);
    DOM.forceSetCookie('stockx_user_logged_in', true);

    if (response.header.authorization) {
      DOM.forceSetCookie('token', response.header.authorization);

      // help-app shared cookie
      DOM.forceSetCookie('loggedIn', customer?.uuid);
    }

    yield put(
      setCustomer({
        ...customer,
        token: response.header.authorization,
      }),
    );
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
    yield put(getMeSuccess());
  } catch (err) {
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
    yield put(getMeFailure());
  }
}

/*
  Auth0 effects
 */
const createAuthClientFor = async (clientType, options = {}) => {
  const auth = (await import('auth0-js')).default;
  const webAuthClient = new auth.WebAuth({
    domain: config.SERVICES.AUTH0.DOMAIN,
    clientID: config.SERVICES.AUTH0.CLIENTID,
    responseType: config.SERVICES.AUTH0.RESPONSETYPE,
    scope: config.SERVICES.AUTH0.SCOPE,
  });

  switch (clientType) {
    case 'universalLogin':
      return webAuthClient.authorize({ ...options });
    case 'parseHash':
      return webAuthClient.parseHash(
        { hash: window.location.hash },
        (err, authResult) => authResult,
      );
    case 'logout':
      return webAuthClient.logout({ ...options });
    case 'resetPassword':
      return new Promise((res, rej) => {
        webAuthClient.changePassword(options, (err, resp) => {
          if (err) {
            rej(err);
          }

          res(resp);
        });
      });

    default:
      return {};
  }
};

export function* onGetAuthHandler() {
  try {
    yield createAuthClientFor('parseHash');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('there was an error', e);
  }
}

export function* onLogOutTry({ payload: { afterPasswordReset = false } }) {
  DOM.delCookie('token');
  DOM.delCookie('cookie_policy_accepted');
  DOM.delCookie('is_gdpr');
  DOM.delCookie('product_page_v2');
  DOM.delCookie('stockx_homepage');
  DOM.delCookie('stockx_product_visits');
  DOM.delCookie('stockx_tos_privacy_accepted');
  DOM.delCookie('stockx_user_logged_in');
  DOM.delCookie('stockx_session');
  DOM.delCookie('userMfaRequested');
  DOM.delCookie('mfaLogin');
  DOM.deleteLocalStorage('updated_profile_check_sms');

  // /help auth cookies
  DOM.deleteHelpCenterAuthCookies();

  yield put(clearCustomer());

  if (typeof window !== 'undefined') {
    const { protocol, host } = window.location;
    const returnTo = afterPasswordReset
      ? `${protocol}//${host}/reset-password-confirmation`
      : `${protocol}//${host}/`;

    createAuthClientFor('logout', { returnTo });
  }
}

export function* onGetUniversalLoginScreen({
  payload: { defaultTab, state = {}, connection },
}) {
  try {
    let redirectUri = '';
    let stockXUrl = '';

    if (typeof window !== 'undefined') {
      const { protocol, host, pathname, search } = window.location;
      const addedQuery = search ? `&${search.substring(1)}` : '';
      const favoriteRedirectData: IFavoriteRedirectData = DOM.getCookie(
        FAVORITE_PRODUCT_REDIRECT_KEY,
      );

      const followRedirectData: IFollowRedirectData = DOM.getCookie(
        FOLLOW_PRODUCT_REDIRECT_KEY,
      );

      let redirectPath = pathname === '/social-confirm' ? '/' : pathname;

      if (favoriteRedirectData?.redirectUrl) {
        redirectPath = favoriteRedirectData.redirectUrl;
      }

      if (followRedirectData?.redirectUrl) {
        redirectPath = followRedirectData.redirectUrl;
      }

      redirectUri = `${protocol}//${host}/callback?path=${redirectPath}${addedQuery}`;
      stockXUrl = `${protocol}//${host}`;
    }

    const sessionId = getOrSetStockXSessionId();

    const isGDPR = yield select(getGDPR);
    const userLang = yield select(getUrlCode);
    const currency = yield select(getSelectedCurrency);
    const countryCode = yield select(getCountryCode);

    const IsEuOrKr =
      countryCode &&
      (EUCountries.includes(countryCode.toUpperCase()) || countryCode === 'KR');

    // @ts-expect-error ts-migrate(2339) FIXME: Property 'state' does not exist on type '{}'.
    const isMfa = !!state.state?.isMfa;
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'state' does not exist on type '{}'.
    const forceLogin = !!state.state?.forceLogin;

    let ui_locales = 'en';
    let lng = 'en';

    // add support for both methods of localization on ULP
    // ui_locales -> auth0 built in localization, lng -> translation file names in auth0 repo
    if (userLang === 'zh-cn') {
      ui_locales = 'zh-CN';
      lng = 'zh';
    } else if (userLang === 'zh-tw') {
      ui_locales = 'zh-TW';
      lng = 'zh-TW';
    } else if (userLang === 'fr-ca') {
      ui_locales = 'fr-CA';
      lng = 'fr-CA';
    } else if (userLang === 'en-gb') {
      lng = 'en-GB';
    } else if (userLang.includes('es')) {
      const [lang, locale] = userLang.split('-');
      const capitalizedLocale = locale.toUpperCase();
      lng = `${lang}-${capitalizedLocale}`;
    } else {
      ui_locales = userLang.split('-')[0];
      lng = ui_locales;
    }

    const baseQueryObject: Dict = {
      'stockx-session-id': sessionId,
      'stockx-default-tab': defaultTab,
      'stockx-is-gdpr': isGDPR,
      'stockx-language': userLang,
      'stockx-url': stockXUrl,
      'stockx-currency': currency,
      'stockx-marketing-opt-in': IsEuOrKr.toString(),
      lng,
      ui_locales,
      redirectUri,
      responseType: 'code',
      responseMode: 'query',
      state: JSON.stringify(state),
      audience: config.SERVICES.AUTH0.AUDIENCE,
    };

    if (isMfa) {
      baseQueryObject.acr_values =
        'http://schemas.openid.net/pape/policies/2007/06/multi-factor';
    } else {
      baseQueryObject.connection = connection;
    }

    if (typeof window !== 'undefined') {
      baseQueryObject['stockx-user-agent'] = window.navigator.userAgent;
    }

    if (forceLogin) {
      if (isTokenExpired()) {
        baseQueryObject.prompt = 'login';
      } else {
        baseQueryObject.prompt = 'none';
      }
    }

    yield createAuthClientFor('universalLogin', baseQueryObject);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('there was an error', e);
  }
}

export const onAuthResetPassword = function* onAuthResetPassword({
  payload: { email, isServerResetPassword },
}) {
  try {
    let customer;
    if (!email) {
      customer = yield select(getCustomer);
    }

    const customerEmail = !email ? customer.email : email;
    const urlPrefix = yield select(getUrlPrefix);

    if (isServerResetPassword) {
      yield apply(client, client.get, [
        `/reset-password`,
        {
          params: {
            email: customerEmail,
          },
        },
      ]);
    } else {
      yield createAuthClientFor('resetPassword', {
        connection: config.SERVICES.AUTH0.CONNECTION,
        email: customerEmail,
      });
    }

    // this prevents login page infinite redirect, but causes quick flash
    history.push(`${urlPrefix}reset-password-confirmation`);

    yield put(logOutTry({ afterPasswordReset: true }));
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }
};
